import React from 'react'
import cx from 'classnames'
import { t } from 'ttag'
import styles from './ScatterPlot.module.scss'
import { useTooltip } from 'hooks/useTooltip'
import { TetherPortal } from 'components/shared/TetherPortal/TetherPortal'
import { Box } from 'components/layout/Box/Box'
import { Arrow } from 'components/shared/Arrow/Arrow'
import { lowercaseFirstLetter } from 'utils/stringFormat'

// Threshold values for match scores (0-100 scale)
// High: 60-100, Medium: 40-60, Low: 0-40
//
// The high cutoff was lowered from 70 to 60. At 70 most talent areas read as a
// medium match, which hid career options the candidate is in fact well suited
// to. The reference implementation in
// docs/backup/talent_assessment/scripts/jobblink_cleaned_20_05_26.R still bands
// at 40 and 70 (`pick_style(report$match[i], 40, 70)`, and the same cutoffs for
// its scatter plot), so these cutoffs now diverge from it on purpose.
//
// Nothing about the match score itself changed. These are reading aids applied to
// a number the backend already produced, which is why the change carries no
// ALGORITHM_VERSION bump and triggers no recalculation: see
// backend/assessments/algorithms/version.py.
export const highValueLimit = 60
export const mediumValueLimit = 40

// Band a match score into high/medium/low. Both the table and the plot band the
// same value here, so a talent can never be shown in two different colours.
// Boundaries work the same way as the R script's: value < 40 is low, value < 60
// is medium.
//
// This also bands older score runs shown through the version selector. Those are
// deliberately read with today's cutoffs rather than the ones in force when they
// were calculated, so that a colour difference between two runs means the scores
// differ, not the presentation.
export const valueLevel = matchScore => {
  if (matchScore >= highValueLimit) return 'high'
  if (matchScore >= mediumValueLimit) return 'medium'
  return 'low'
}

const Dot = ({ x, y, matchScore, description, area, key }) => {
  const {
    state: {
      arrowRef,
      arrowStyle,
      floating,
      floatingStyle,
      reference,
      hasArrow,
      isToggled,
    },
    handlers: {
      handleBlur,
      handleFocus,
      handleMouseEnter,
      handleMouseLeave,
      handleTouchEnd,
      handleTouchStart,
    },
  } = useTooltip({ initialPlacement: 'bottom' })

  return (
    <>
      <div
        className={cx(styles.dot, {
          [styles.green]: valueLevel(matchScore) === 'high',
          [styles.yellow]: valueLevel(matchScore) === 'medium',
          [styles.red]: valueLevel(matchScore) === 'low',
        })}
        {...{
          ref: reference,
          onBlur: handleBlur,
          onFocus: handleFocus,
          onMouseEnter: handleMouseEnter,
          onMouseLeave: handleMouseLeave,
          onTouchStart: handleTouchStart,
          onTouchEnd: handleTouchEnd,
        }}
        key={key}
        style={{
          left: `calc(${y}% - 8px)`,
          bottom: `calc(${x}% - 8px)`,
        }}
      ></div>
      <TetherPortal isOpen={isToggled}>
        <Box
          align="center"
          className={styles.message}
          justify="center"
          ref={floating}
          // TODO(a11y): was role="status", which <Box> silently drops. Restore as
          // ariaRole once the DOM change can be verified in the UI.
          style={floatingStyle}
        >
          {hasArrow && (
            <Arrow
              ref={arrowRef}
              size="small"
              style={arrowStyle}
              theme="bright"
            />
          )}
          <Box
            align="center"
            className={styles.content}
            direction="column"
            justify="center"
            // TODO(a11y): was role="tooltip", which <Box> silently drops. Restore as
            // ariaRole once the DOM change can be verified in the UI.
          >
            <div>{`${area} - ${lowercaseFirstLetter(description)}`}</div>
          </Box>
        </Box>
      </TetherPortal>
    </>
  )
}

export const ScatterPlot = ({ data }) => {
  const lineIndexes = [...Array(5).keys()]

  return (
    <div className={cx(styles.container)}>
      {data.map(({ dataPoint, matchScore, description, area }, index) => {
        return (
          <Dot
            key={index}
            x={dataPoint[0]}
            y={dataPoint[1]}
            matchScore={matchScore}
            area={area}
            description={description}
          />
        )
      })}
      <div className={styles.horizontalLines}>
        {lineIndexes.map(index => (
          <div
            key={index}
            className={cx(styles.horizontalLine, {
              [styles.axis]: index === lineIndexes.length - 1,
            })}
          ></div>
        ))}
      </div>
      <div className={styles.verticalLines}>
        {lineIndexes.map(index => (
          <div key={index} className={cx(styles.verticalLine)}></div>
        ))}
      </div>
      <div className={styles.markers}>
        {lineIndexes.map(index => (
          <div key={index} className={cx(styles.marker)}></div>
        ))}
      </div>
      <div className={styles.verticalSteps}>
        <div className={styles.verticalLabel}>{t`Interest match`}</div>
        {lineIndexes.map(index => (
          <div key={index} className={cx(styles.verticalStep)}>
            {index * 25}
          </div>
        ))}
      </div>
      <div className={styles.horizontalSteps}>
        <div className={styles.horizontalLabel}>{t`Personality match`}</div>
        {lineIndexes.map(index => (
          <div key={index} className={cx(styles.horizontalStep)}>{`${
            100 - index * 25
          }`}</div>
        ))}
      </div>
    </div>
  )
}
